import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, IndianRupee, Receipt, TrendingUp, Wallet } from 'lucide-react';
import { StatCard } from '../../common/page_component/index.js';
import dashboardService from '../../../services/DashboardService.js';
import { fullCurrency } from '../../../utils/formatCurrency.js';
import { buildDateParams } from './DateRangeFilter.js';

const money = (value) => fullCurrency(value);

const rateText = (part, whole) => {
  const base = Number(whole) || 0;
  if (!base) return '0%';
  return `${(((Number(part) || 0) / base) * 100).toFixed(1)}%`;
};

export function OwnerFinanceSummary({ data, variant = 'full' }) {
  const navigate = useNavigate();
  const hubAdmin = variant === 'hub';

  return (
    <div className="space-y-4">
      <div className={`grid grid-cols-2 ${hubAdmin ? 'sm:grid-cols-2' : 'xl:grid-cols-5'} gap-3`}>
        {!hubAdmin && (
          <StatCard label="Total sales" value={money(data?.sales)} icon={<IndianRupee className="w-4 h-4 text-white" />} color="bg-green-600" />
        )}
        {!hubAdmin && (
          <StatCard
            label="Commission"
            value={money(data?.commission)}
            hint={`${rateText(data?.commission, data?.sales)} of sales`}
            detail={`TDS ${money(data?.tds)}`}
            icon={<Wallet className="w-4 h-4 text-white" />}
            color="bg-amber-500"
          />
        )}
        <StatCard
          label="GST"
          value={money(data?.gst)}
          hint="Company GST on shipments"
          detail={`${Number(data?.gst_count || 0)} shipments`}
          icon={<Receipt className="w-4 h-4 text-white" />}
          color="bg-violet-600"
        />
        <StatCard
          label="Operating expense"
          value={money(data?.expense)}
          hint={`${rateText(data?.expense, data?.sales)} of sales · approved only`}
          detail={`Pending review ${money(data?.pending_expense?.amount)}`}
          detailClassName="text-amber-700"
          icon={<Building2 className="w-4 h-4 text-white" />}
          color="bg-red-500"
        />
        {!hubAdmin && (
          <StatCard
            label="Net P&L"
            value={money(data?.net)}
            hint={`${rateText(data?.net, data?.sales)} of sales`}
            icon={<TrendingUp className="w-4 h-4 text-white" />}
            color="bg-blue-600"
          />
        )}
      </div>

      {!hubAdmin && (
      <button
        type="button"
        onClick={() => navigate('/agent-commission-breakdown')}
        className="w-full text-left bg-white rounded-xl border border-gray-100 p-4 hover:border-teal-200 hover:bg-teal-50/40"
      >
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold text-gray-800">Agent commission</h2>
            <p className="text-xs text-gray-500 mt-1">Same breakdown as the admin commission page. Open it to see each agent.</p>
          </div>
          <span className="text-xs font-semibold text-teal-700">Open agent commission</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
          <div>
            <p className="text-[11px] uppercase tracking-wide text-gray-500">Total</p>
            <p className="text-lg font-bold text-gray-900">{money(data?.commission)}</p>
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-wide text-gray-500">Paid</p>
            <p className="text-lg font-bold text-gray-900">{money(data?.commission_paid)}</p>
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-wide text-gray-500">Unpaid</p>
            <p className="text-lg font-bold text-gray-900">{money(data?.commission_unpaid)}</p>
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-wide text-gray-500">TDS</p>
            <p className="text-lg font-bold text-gray-900">{money(data?.tds)}</p>
          </div>
        </div>
      </button>
      )}
    </div>
  );
}

export function OwnerFinancePanel({ dateRange, customStart, customEnd, variant = 'full' }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const response = await dashboardService.getOwner(buildDateParams(dateRange, customStart, customEnd));
        if (!cancelled) setData(response?.data || null);
      } catch {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [dateRange, customStart, customEnd]);

  return (
    <div className={loading ? 'opacity-70' : ''}>
      <OwnerFinanceSummary data={data} variant={variant} />
    </div>
  );
}
